import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Image,
  IconButton,
  HStack,
  Text,
  useBreakpointValue
} from '@chakra-ui/react'
import { useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'

interface ImageViewerModalProps {
  isOpen: boolean
  onClose: () => void
  images: string[]
  initialIndex?: number
}

const ImageViewerModal = ({ isOpen, onClose, images, initialIndex = 0 }: ImageViewerModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const isMobile = useBreakpointValue({ base: true, md: false })

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrevious()
    } else if (e.key === 'ArrowRight') {
      handleNext()
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
    >
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        maxW="100vw"
        maxH="100vh"
        m={0}
        borderRadius={0}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <ModalCloseButton
          color="white"
          bg="blackAlpha.500"
          _hover={{ bg: "blackAlpha.700" }}
          size="lg"
          top={4}
          right={4}
          zIndex={10}
        />
        
        <ModalBody
          p={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          {/* 이전 버튼 */}
          {images.length > 1 && (
            <IconButton
              aria-label="이전 이미지"
              icon={<ChevronLeftIcon />}
              position="absolute"
              left={4}
              top="50%"
              transform="translateY(-50%)"
              bg="blackAlpha.500"
              color="white"
              _hover={{ bg: "blackAlpha.700" }}
              size="lg"
              zIndex={10}
              onClick={handlePrevious}
            />
          )}

          {/* 이미지 */}
          <Box
            maxW="90vw"
            maxH="90vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={images[currentIndex]}
              alt={`이미지 ${currentIndex + 1}`}
              maxW="100%"
              maxH="100%"
              objectFit="contain"
              borderRadius="lg"
              boxShadow="0 10px 30px rgba(0,0,0,0.5)"
            />
          </Box>

          {/* 다음 버튼 */}
          {images.length > 1 && (
            <IconButton
              aria-label="다음 이미지"
              icon={<ChevronRightIcon />}
              position="absolute"
              right={4}
              top="50%"
              transform="translateY(-50%)"
              bg="blackAlpha.500"
              color="white"
              _hover={{ bg: "blackAlpha.700" }}
              size="lg"
              zIndex={10}
              onClick={handleNext}
            />
          )}

          {/* 이미지 카운터 */}
          {images.length > 1 && (
            <Box
              position="absolute"
              bottom={4}
              left="50%"
              transform="translateX(-50%)"
              bg="blackAlpha.500"
              color="white"
              px={4}
              py={2}
              borderRadius="full"
              fontSize="sm"
              fontWeight="medium"
            >
              {currentIndex + 1} / {images.length}
            </Box>
          )}

          {/* 모바일에서 스와이프 안내 */}
          {isMobile && images.length > 1 && (
            <Box
              position="absolute"
              top={4}
              left="50%"
              transform="translateX(-50%)"
              bg="blackAlpha.500"
              color="white"
              px={3}
              py={1}
              borderRadius="md"
              fontSize="xs"
            >
              좌우로 스와이프하여 이미지 변경
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default ImageViewerModal
